import Link from "next/link";
import Image from "next/image";
import { Blog } from "@/Types/BlogTypes";
import { ArrowRight, Calendar } from "lucide-react";
import { stripHtml, truncateText, truncateHeading } from "@/lib/utils/cleanTextUtility";
import { getImageUrl } from "@/lib/imageUrl";

interface BlogCardProps {
    blog: Blog;
}

export default function BlogCard({ blog }: BlogCardProps) {
    const formatDate = (dateString: string | Date) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Extract and clean text from blogContent
    const getExcerpt = () => {
        if (blog.blogExcerpt) {
            const cleanExcerpt = stripHtml(blog.blogExcerpt);
            return truncateText(cleanExcerpt, 150);
        }
        
        if (typeof blog.blogContent === 'string') {
            const cleanContent = stripHtml(blog.blogContent);
            return truncateText(cleanContent, 150);
        }
        
        // If blogContent is an object (like Quill delta or other rich text format)
        // Try to extract plain text
        if (blog.blogContent && typeof blog.blogContent === 'object') {
            // Handle Quill delta format
            if (blog.blogContent.ops) {
                const text = blog.blogContent.ops
                    .map((op: any) => typeof op.insert === 'string' ? op.insert : '')
                    .join('');
                return truncateText(text, 150);
            }
        }
        
        return '';
    };

    // Clean and truncate the title
    const cleanTitle = truncateHeading(blog.blogTitle, 60);

    return (
        <Link 
            href={`/insights/${blog.blogSlug}`}
            className="group flex flex-col bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 h-full"
        >
            {/* Blog Image */}
            <div className="relative w-full h-48 md:h-56 overflow-hidden bg-gray-200">
                {blog.featuredImage ? (
                    <Image
                        src={getImageUrl(blog.featuredImage)}
                        alt={blog.blogTitle}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                ) : (
                    <div className="flex items-center justify-center w-full h-full bg-gray-300 text-gray-500">
                        No Image
                    </div>
                )}
            </div>

            {/* Blog Content */}
            <div className="flex flex-col p-6 flex-1">
                {/* Meta Info */}
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(blog.createdAt)}</span>
                </div>

                {/* Title */}
                <h3 className="text-xl font-semibold mb-3 line-clamp-2 group-hover:text-secondary transition-colors">
                    {cleanTitle}
                </h3>

                {/* Excerpt */}
                {getExcerpt() && (
                    <p className="text-gray-600 text-sm line-clamp-3 mb-4 flex-1">
                        {getExcerpt()}
                    </p>
                )}

                {/* Read More */}
                <span className="flex items-center gap-2 text-secondary font-medium text-sm group-hover:underline">
                    Read More <ArrowRight />
                </span>
            </div>
        </Link>
    );
}